import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const WEBHOOK_URL =
  process.env.REACT_APP_CHAT_WEBHOOK_URL ||
  'https://n8n.staging.wayabank.ng/webhook/waya/website-assistant';
const WEBHOOK_SECRET =
  process.env.REACT_APP_CHAT_WEBHOOK_SECRET ||
  'bf5b7936e675a476055ea072dc5a3ac8639f27a5fe6e1d00875c462f2bd2955f';
const REQUEST_TIMEOUT_MS = 30000;
const SESSION_STORAGE_KEY = 'waya_chat_session_id';
const ATTRIBUTION_STORAGE_KEY = 'waya_chat_attribution';

const WELCOME_MESSAGE = {
  role: 'assistant',
  text: "Hi! I'm the WayaBank assistant. Ask me anything about opening an account, sending money, loans, or business banking.",
  chips: [
    'How do I open an account?',
    'What are your loan options?',
    'How do I contact support?',
  ],
};

const genId = () =>
  'sess_' +
  Math.random().toString(36).slice(2, 10) +
  Date.now().toString(36);

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!sid) {
      sid = genId();
      sessionStorage.setItem(SESSION_STORAGE_KEY, sid);
    }
    return sid;
  } catch (e) {
    return genId();
  }
};

const captureAttribution = () => {
  try {
    const existing = sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (existing) return JSON.parse(existing);

    const params = new URLSearchParams(window.location.search);
    const utm = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(
      (k) => {
        const v = params.get(k);
        if (v) utm[k.replace('utm_', '')] = v;
      },
    );

    const data = {
      landing_url: window.location.href,
      referrer: document.referrer || undefined,
      utm: Object.keys(utm).length ? utm : undefined,
    };
    sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(data));
    return data;
  } catch (e) {
    return {};
  }
};

function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [wantsHumanPrompt, setWantsHumanPrompt] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', phone: '' });
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  const sessionIdRef = useRef(null);
  const attributionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const brand = 'wayabank';

  useEffect(() => {
    sessionIdRef.current = getSessionId();
    attributionRef.current = captureAttribution();

    window.wayaChat = {
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((v) => !v),
    };
    return () => {
      if (window.wayaChat) delete window.wayaChat;
    };
  }, []);

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 200);
    }
  }, [isOpen, messages, isSending]);

  const sendMessage = useCallback(
    async (rawText) => {
      const text = (rawText || '').trim();
      if (!text || isSending) return;

      setMessages((prev) => [...prev, { role: 'user', text }]);
      setInput('');
      setIsSending(true);

      const body = {
        brand,
        message: text,
        session_id: sessionIdRef.current,
        ...(leadForm.name && { name: leadForm.name }),
        ...(leadForm.email && { email: leadForm.email }),
        ...(leadForm.phone && { phone: leadForm.phone }),
        ...(attributionRef.current && attributionRef.current),
      };

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

      try {
        const res = await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-waya-secret': WEBHOOK_SECRET,
          },
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();

        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: data.reply || "Sorry, I didn't get that. Could you rephrase?",
            chips: Array.isArray(data.suggested_questions)
              ? data.suggested_questions.slice(0, 4)
              : [],
          },
        ]);

        if (data.wants_human) {
          setWantsHumanPrompt(true);
        }
      } catch (err) {
        const isTimeout = err.name === 'AbortError';
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            text: isTimeout
              ? 'Sorry, that took too long. Please try again or contact us at support@wayabank.ng.'
              : "I'm having trouble connecting right now. Please try again in a moment.",
            chips: [],
          },
        ]);
      } finally {
        clearTimeout(timeoutId);
        setIsSending(false);
      }
    },
    [isSending, leadForm.name, leadForm.email, leadForm.phone],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleChip = (chip) => sendMessage(chip);

  const handleConnectToTeam = () => {
    setShowLeadForm(true);
    setWantsHumanPrompt(false);
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadForm.email) return;
    setLeadSubmitted(true);
    setShowLeadForm(false);
    sendMessage(
      `Please connect me to a human agent. My email is ${leadForm.email}${leadForm.name ? `, name: ${leadForm.name}` : ''
      }${leadForm.phone ? `, phone: ${leadForm.phone}` : ''}.`,
    );
  };

  const lastAssistantChips = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && messages[i].chips && messages[i].chips.length) {
        return messages[i].chips;
      }
      if (messages[i].role === 'user') break;
    }
    return [];
  }, [messages]);

  return (
    <>
      <button
        type="button"
        className={`waya-chat-fab ${isOpen ? 'waya-chat-fab--open' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
            <path
              d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      <div
        className={`waya-chat-panel ${isOpen ? 'waya-chat-panel--open' : ''}`}
        role="dialog"
        aria-label="WayaBank chat assistant"
        aria-hidden={!isOpen}
      >
        <div className="waya-chat-header">
          <div className="waya-chat-header__avatar">W</div>
          <div className="waya-chat-header__meta">
            <div className="waya-chat-header__title">WayaBank Assistant</div>
            <div className="waya-chat-header__status">
              <span className="waya-chat-header__dot" /> Online
            </div>
          </div>
          <button
            type="button"
            className="waya-chat-header__close"
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="waya-chat-body">
          {messages.map((m, i) => (
            <div key={i} className={`waya-chat-msg waya-chat-msg--${m.role}`}>
              <div className="waya-chat-msg__bubble">{m.text}</div>
            </div>
          ))}

          {isSending && (
            <div className="waya-chat-msg waya-chat-msg--assistant">
              <div className="waya-chat-msg__bubble waya-chat-typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          {!isSending && lastAssistantChips.length > 0 && (
            <div className="waya-chat-chips">
              {lastAssistantChips.map((chip, i) => (
                <button
                  key={i}
                  type="button"
                  className="waya-chat-chip"
                  onClick={() => handleChip(chip)}
                >
                  {chip}
                </button>
              ))}
            </div>
          )}

          {wantsHumanPrompt && !showLeadForm && !leadSubmitted && (
            <div className="waya-chat-handoff">
              <p>Would you like to connect with a human agent?</p>
              <button
                type="button"
                className="waya-chat-handoff__btn"
                onClick={handleConnectToTeam}
              >
                Connect to team
              </button>
            </div>
          )}

          {showLeadForm && (
            <form className="waya-chat-lead-form" onSubmit={handleLeadSubmit}>
              <p>Leave your details and our team will reach out.</p>
              <input
                type="text"
                placeholder="Your name"
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
              />
              <input
                type="email"
                placeholder="Email address *"
                required
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={leadForm.phone}
                onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
              />
              <button type="submit" className="waya-chat-lead-form__submit">
                Send
              </button>
            </form>
          )}

          <div ref={messagesEndRef} />
        </div>

        <form className="waya-chat-input" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Type your message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={isSending}
            aria-label="Message"
          />
          <button
            type="submit"
            className="waya-chat-send"
            disabled={isSending || !input.trim()}
            aria-label="Send message"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </form>
      </div>
    </>
  );
}

export default ChatWidget;
